import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertCircle, AlertTriangle, Info, CheckCircle, Download, Clock, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ValidatorPage = () => {
  const { projectId } = useParams();
  const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    fetchValidationResult();
  }, [projectId]);

  const fetchValidationResult = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/validation`);
      setValidationResult(response.data);
    } catch (error) {
      setValidationResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const response = await axios.post(`${API_URL}/projects/${projectId}/validate`);
      setValidationResult(response.data);
      if (response.data.total_errors === 0 && response.data.total_warnings === 0) {
        toast.success('Validación completada sin errores ni advertencias');
      } else if (response.data.total_errors === 0) {
        toast.warning(`Validación completada con ${response.data.total_warnings} advertencias`);
      } else {
        toast.error(`Validación completada con ${response.data.total_errors} errores`);
      }
    } catch (error) {
      toast.error('Error al validar el feed GTFS');
    } finally {
      setValidating(false);
    }
  };

  const handleExportResults = () => {
    if (!validationResult) return;

    const text = `Resultados de Validación GTFS\n` +
      `${'='.repeat(40)}\n\n` +
      `Total de errores: ${validationResult.total_errors}\n` +
      `Total de advertencias: ${validationResult.total_warnings}\n` +
      `Fecha: ${new Date(validationResult.validated_at).toLocaleString()}\n\n` +
      `Problemas encontrados:\n${'─'.repeat(40)}\n\n` +
      (validationResult.issues.length === 0
        ? 'No se encontraron problemas.\n'
        : validationResult.issues.map((issue, i) =>
            `${i + 1}. [${issue.severity.toUpperCase()}] ${issue.file}\n   ${issue.message}\n`
          ).join('\n'));

    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `validation_results_${projectId}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Resultados exportados');
  };

  const groupedIssues = validationResult?.issues.reduce((acc, issue) => {
    if (!acc[issue.file]) acc[issue.file] = [];
    acc[issue.file].push(issue);
    return acc;
  }, {}) || {};

  const getIcon = (severity) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />;
      default:
        return <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />;
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'error':
        return <Badge variant="destructive" className="text-[10px]">Error</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-100 text-yellow-800 text-[10px]">Advertencia</Badge>;
      default:
        return <Badge variant="secondary" className="text-[10px]">Info</Badge>;
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight mb-2" data-testid="validator-title">Validador GTFS</h1>
        <p className="text-sm text-slate-600">Valida tu feed GTFS según las especificaciones oficiales</p>
      </div>

      {/* Actions */}
      <div className="mb-6 flex gap-3">
        <Button
          data-testid="validate-button"
          onClick={handleValidate}
          disabled={validating}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {validating ? 'Validando...' : 'Ejecutar Validación'}
        </Button>
        {validationResult && (
          <Button data-testid="export-results-button" onClick={handleExportResults} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Exportar Resultados
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-600">Cargando...</div>
      ) : !validationResult ? (
        <Card className="p-8" data-testid="no-validation-results">
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600">No hay resultados de validación aún</p>
            <p className="text-sm text-slate-500 mt-1">Haz clic en &quot;Ejecutar Validación&quot; para comenzar</p>
          </div>
        </Card>
      ) : (
        <div data-testid="validation-results">
          {/* Validation date */}
          <div className="flex items-center gap-2 mb-4 text-sm text-slate-500">
            <Clock className="w-4 h-4" />
            <span>Última validación: {new Date(validationResult.validated_at).toLocaleString()}</span>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-6" data-testid="errors-count-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Errores</p>
                  <p className="text-2xl font-bold" data-testid="errors-count">{validationResult.total_errors}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6" data-testid="warnings-count-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Advertencias</p>
                  <p className="text-2xl font-bold" data-testid="warnings-count">{validationResult.total_warnings}</p>
                </div>
              </div>
            </Card>

            <Card className={`p-6 ${validationResult.total_errors === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="status-card">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${validationResult.total_errors === 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <CheckCircle className={`w-6 h-6 ${validationResult.total_errors === 0 ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Estado</p>
                  <p className={`text-lg font-semibold ${validationResult.total_errors === 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {validationResult.total_errors === 0 ? 'Feed válido' : 'Con errores'}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Issues by file */}
          <div className="space-y-4" data-testid="issues-list">
            {Object.entries(groupedIssues).map(([file, issues]) => (
              <Card key={file} className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-slate-500" />
                  <h3 className="text-lg font-semibold" data-testid={`file-issues-${file}`}>{file}</h3>
                  <Badge variant="secondary" className="text-xs">{issues.length} problemas</Badge>
                </div>
                <div className="space-y-2">
                  {issues.map((issue, index) => (
                    <div
                      key={index}
                      data-testid={`issue-${file}-${index}`}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        issue.severity === 'error' ? 'bg-red-50' : issue.severity === 'warning' ? 'bg-yellow-50' : 'bg-slate-50'
                      }`}
                    >
                      {getIcon(issue.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {getSeverityBadge(issue.severity)}
                          {issue.line && (
                            <span className="text-xs text-slate-500">Línea: {issue.line}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-800">{issue.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}

            {validationResult.issues.length === 0 && (
              <Card className="p-8 bg-green-50 border-green-200" data-testid="no-issues-card">
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
                  <p className="text-xl font-semibold text-green-800">Feed GTFS válido</p>
                  <p className="text-sm text-green-600 mt-1">No se encontraron errores ni advertencias</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidatorPage;
